import { useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import { SITE_URL } from "@/lib/siteConfig";
import {
  ArrowRight, Search, Check, ExternalLink, Zap,
  ShoppingCart, CreditCard, Globe, Code, Mail,
  MessageSquare, BarChart3, Users, Plug, Webhook,
} from "lucide-react";

// ── Integration categories and data ──
const CATEGORIES = [
  { id: "all",         label: "All",           icon: Globe },
  { id: "ecommerce",   label: "E-commerce",    icon: ShoppingCart },
  { id: "crm",         label: "CRM & Sales",   icon: Users },
  { id: "payments",    label: "Payments",       icon: CreditCard },
  { id: "analytics",   label: "Analytics",      icon: BarChart3 },
  { id: "messaging",   label: "Messaging",      icon: MessageSquare },
  { id: "automation",  label: "Automation",      icon: Zap },
  { id: "developer",   label: "Developer",      icon: Code },
];

interface IntegrationEntry {
  name: string;
  category: string;
  description: string;
  method: "native" | "api" | "webhook" | "zapier";
  popular?: boolean;
}

const INTEGRATIONS: IntegrationEntry[] = [
  // E-commerce
  { name: "Shopify",          category: "ecommerce",  description: "Sync customers, track purchases, and trigger abandoned cart emails automatically.",       method: "webhook",  popular: true },
  { name: "WooCommerce",      category: "ecommerce",  description: "Connect your WordPress store to sync orders, customers, and product data.",               method: "webhook",  popular: true },
  { name: "Stripe",           category: "payments",   description: "Track purchases, subscription events, and revenue attribution from Stripe payments.",     method: "webhook",  popular: true },
  { name: "Gumroad",          category: "ecommerce",  description: "Automatically add buyers to your list and trigger post-purchase sequences.",              method: "webhook" },
  { name: "Lemonsqueezy",     category: "ecommerce",  description: "Sync digital product sales and subscription events to your email list.",                  method: "webhook" },
  { name: "BigCommerce",      category: "ecommerce",  description: "Connect your BigCommerce store for customer sync and purchase-triggered emails.",         method: "webhook" },
  { name: "Etsy",             category: "ecommerce",  description: "Import Etsy customers and send follow-up emails after purchases.",                        method: "zapier" },

  // CRM & Sales
  { name: "HubSpot",          category: "crm",        description: "Sync contacts and deal stages between MoreMail and HubSpot CRM.",                        method: "zapier",   popular: true },
  { name: "Salesforce",       category: "crm",        description: "Push email engagement data to Salesforce leads and contacts.",                            method: "zapier" },
  { name: "Pipedrive",        category: "crm",        description: "Sync subscriber activity with your Pipedrive deals and contacts.",                        method: "zapier" },
  { name: "Close CRM",        category: "crm",        description: "Connect Close CRM to sync leads and automate outreach sequences.",                       method: "zapier" },

  // Payments
  { name: "PayPal",           category: "payments",   description: "Track PayPal transactions and trigger post-purchase automations.",                        method: "webhook" },
  { name: "Square",           category: "payments",   description: "Connect Square POS data for in-store customer email marketing.",                          method: "zapier" },

  // Analytics
  { name: "Google Analytics",  category: "analytics", description: "Track email campaign traffic and conversions in Google Analytics with UTM parameters.",   method: "native",   popular: true },
  { name: "Facebook Pixel",    category: "analytics", description: "Fire Facebook conversion events from email clicks for retargeting and lookalike audiences.", method: "native" },
  { name: "Mixpanel",          category: "analytics", description: "Send email engagement events to Mixpanel for product analytics and cohort analysis.",    method: "webhook" },
  { name: "Segment",           category: "analytics", description: "Route email events through Segment to your entire analytics stack.",                      method: "webhook" },

  // Messaging
  { name: "Twilio SMS",       category: "messaging",  description: "Send SMS messages as part of your automation workflows. Native integration.",             method: "native",   popular: true },
  { name: "Vonage SMS",       category: "messaging",  description: "Alternative SMS provider for automation workflows. Native integration.",                  method: "native" },
  { name: "LinkedIn",         category: "messaging",  description: "Send LinkedIn messages as automation steps for B2B outreach sequences.",                  method: "native" },
  { name: "Slack",            category: "messaging",  description: "Get real-time Slack notifications for campaign sends, replies, and subscriber events.",   method: "webhook" },
  { name: "Discord",          category: "messaging",  description: "Post email campaign updates and subscriber alerts to Discord channels.",                  method: "webhook" },

  // Automation
  { name: "Zapier",           category: "automation", description: "Connect MoreMail to 5,000+ apps. Trigger zaps on sends, opens, clicks, and replies.",    method: "native",   popular: true },
  { name: "Make (Integromat)", category: "automation", description: "Build complex multi-step workflows connecting MoreMail to hundreds of services.",        method: "webhook" },
  { name: "n8n",              category: "automation",  description: "Self-hosted workflow automation. Connect via webhooks for full control.",                 method: "webhook" },

  // Developer
  { name: "REST API",         category: "developer",  description: "Full REST API for subscribers, campaigns, automations, analytics, and more.",             method: "api",      popular: true },
  { name: "Webhooks",         category: "developer",  description: "Real-time webhook events for sends, opens, clicks, bounces, replies, and unsubscribes.", method: "native",   popular: true },
  { name: "WordPress Plugin", category: "developer",  description: "Add signup forms and subscriber sync to any WordPress site.",                             method: "native" },
  { name: "JavaScript SDK",   category: "developer",  description: "Embed signup forms, track events, and manage subscribers from any website.",              method: "api" },
  { name: "CSV Import/Export", category: "developer", description: "Bulk import subscribers via CSV. Export lists, campaigns, and analytics data anytime.",   method: "native" },
];

const METHOD_BADGE: Record<string, { label: string; color: string }> = {
  native:  { label: "Native",  color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  api:     { label: "API",     color: "bg-blue-50 text-blue-700 border-blue-200" },
  webhook: { label: "Webhook", color: "bg-amber-50 text-amber-700 border-amber-200" },
  zapier:  { label: "Zapier",  color: "bg-purple-50 text-purple-700 border-purple-200" },
};

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "MoreMail Integrations",
  applicationCategory: "BusinessApplication",
  description: `MoreMail integrates with ${INTEGRATIONS.length}+ platforms including Shopify, Stripe, Zapier, Twilio, and more. Connect your entire stack.`,
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

export default function IntegrationDirectoryPage() {
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = INTEGRATIONS.filter(i => {
    if (category !== "all" && i.category !== category) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase()) && !i.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const popularCount = INTEGRATIONS.filter(i => i.popular).length;

  return (
    <>
      <SeoHead
        title={`${INTEGRATIONS.length}+ Integrations — Connect MoreMail to Your Entire Stack`}
        description={`MoreMail integrates with ${INTEGRATIONS.length}+ platforms including Shopify, Stripe, WooCommerce, Zapier, Twilio, and more. Native integrations, webhooks, and API access.`}
        canonical={`${SITE_URL}/integrations-directory`}
        keywords="MoreMail integrations, email marketing integrations, Shopify email integration, Stripe email integration, Zapier email marketing, WooCommerce email, email API, webhook email marketing"
        ogTitle={`${INTEGRATIONS.length}+ Integrations — Connect MoreMail to Everything | MoreMail`}
        ogDescription="Shopify, Stripe, Zapier, Twilio, WordPress, and more. Connect your entire stack to MoreMail."
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="pt-16 pb-12 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 backdrop-blur-sm">
            <Plug size={13} className="text-coral" />
            {INTEGRATIONS.length}+ integrations
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
            Connects to <span className="text-coral">Everything</span>
          </h1>
          <p className="text-white/50 text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            Native integrations, webhooks, a full REST API, and Zapier support.
            MoreMail plugs into your existing stack — no rip and replace.
          </p>
          <Link to="/register"
            className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-7 py-3.5 rounded-lg text-sm font-bold transition-colors shadow-lg shadow-coral/20">
            Start Free <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* Search + Filter */}
      <section className="py-8 bg-white border-b border-sand">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate/40" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search integrations..."
                className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-sand text-sm font-body focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral transition-all" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const active = category === cat.id;
              const count = cat.id === "all" ? INTEGRATIONS.length : INTEGRATIONS.filter(i => i.category === cat.id).length;
              return (
                <button key={cat.id} onClick={() => setCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-all border ${
                    active ? "bg-ink text-white border-ink" : "bg-white border-sand text-slate hover:border-ink/30"
                  }`}>
                  <Icon size={13} /> {cat.label} <span className="opacity-50">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-10 bg-paper">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-xs text-slate mb-4">{filtered.length} integration{filtered.length !== 1 ? "s" : ""}</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(integration => {
              const badge = METHOD_BADGE[integration.method];
              return (
                <div key={integration.name}
                  className="bg-white rounded-xl border border-sand p-5 hover:shadow-md hover:border-coral/30 transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display font-bold text-ink text-sm">{integration.name}</h3>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.color}`}>
                      {badge.label}
                    </span>
                  </div>
                  <p className="text-xs text-slate leading-relaxed mb-3">{integration.description}</p>
                  {integration.popular && (
                    <span className="text-[10px] font-semibold text-coral">Popular</span>
                  )}
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12">
              <Search size={32} className="mx-auto text-slate/20 mb-3" />
              <p className="text-sm text-slate">No integrations match your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* How to connect */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            Four Ways to <span className="text-coral">Connect</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { method: "Native", desc: "Built-in integrations that work out of the box. Connect in your settings with a few clicks.", color: "bg-emerald-50 border-emerald-200", icon: Plug },
              { method: "Webhooks", desc: "Real-time event notifications. Configure endpoints to receive data on sends, opens, clicks, bounces, and replies.", color: "bg-amber-50 border-amber-200", icon: Webhook },
              { method: "REST API", desc: "Full programmatic access. Manage subscribers, send campaigns, query analytics, and more from any language.", color: "bg-blue-50 border-blue-200", icon: Code },
              { method: "Zapier", desc: "Connect to 5,000+ apps without writing code. Trigger zaps on any email event and push data anywhere.", color: "bg-purple-50 border-purple-200", icon: Zap },
            ].map(item => (
              <div key={item.method} className={`rounded-xl border p-5 ${item.color}`}>
                <item.icon size={20} className="text-ink mb-3" />
                <h3 className="font-display font-bold text-ink text-sm mb-1.5">{item.method}</h3>
                <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Your Stack, Connected
          </h2>
          <p className="text-white/50 text-base mb-8">
            {INTEGRATIONS.length}+ integrations. Native connections, webhooks, API, and Zapier.
            MoreMail fits into your workflow — not the other way around.
          </p>
          <div className="flex justify-center mb-6">
            <LeadCaptureBox />
          </div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
